import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { createPortal } from 'react-dom';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info' | 'loading';
  title: string;
  message?: string;
  duration?: number; // 0 for persistent
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastState {
  toasts: Toast[];
}

type ToastAction =
  | { type: 'ADD_TOAST'; payload: Toast }
  | { type: 'REMOVE_TOAST'; payload: string }
  | { type: 'CLEAR_ALL' };

const ToastContext = createContext<{
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
  success: (title: string, message?: string) => string;
  error: (title: string, message?: string) => string;
  warning: (title: string, message?: string) => string;
  info: (title: string, message?: string) => string;
  loading: (title: string, message?: string) => string;
} | undefined>(undefined);

// Global timer store to persist across re-renders
const toastTimers = new Map<string, NodeJS.Timeout>();

const toastReducer = (state: ToastState, action: ToastAction): ToastState => {
  switch (action.type) {
    case 'ADD_TOAST':
      return {
        ...state,
        toasts: [...state.toasts, action.payload],
      };
    case 'REMOVE_TOAST':
      return {
        ...state,
        toasts: state.toasts.filter(toast => toast.id !== action.payload),
      };
    case 'CLEAR_ALL':
      return {
        ...state,
        toasts: [],
      };
    default:
      return state;
  }
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(toastReducer, { toasts: [] });

  // Cleanup timers when toasts are removed
  React.useEffect(() => {
    const activeToastIds = new Set(state.toasts.map(t => t.id));
    
    // Clear timers for toasts that no longer exist
    for (const [id, timer] of toastTimers.entries()) {
      if (!activeToastIds.has(id)) {
        console.log(`[TOAST DEBUG] Cleaning up orphaned timer for toast ${id}`);
        clearTimeout(timer);
        toastTimers.delete(id);
      }
    }
  }, [state.toasts]);

  const addToast = useCallback((toastData: Omit<Toast, 'id'>): string => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const toast: Toast = {
      id,
      duration: 5000,
      dismissible: true,
      ...toastData,
    };

    console.log(`[TOAST DEBUG] Creating toast "${toast.title}" with ID ${id}, duration: ${toast.duration}ms`);
    dispatch({ type: 'ADD_TOAST', payload: toast });

    // Auto-dismiss if duration is set - use global timer store
    if (toast.duration && toast.duration > 0) {
      console.log(`[TOAST DEBUG] Setting timeout for toast ${id} - will dismiss in ${toast.duration}ms`);
      
      // Clear any existing timer for this ID (shouldn't happen, but safety)
      const existingTimer = toastTimers.get(id);
      if (existingTimer) {
        console.log(`[TOAST DEBUG] Clearing existing timer for toast ${id}`);
        clearTimeout(existingTimer);
      }
      
      const timeoutId = setTimeout(() => {
        console.log(`[TOAST DEBUG] Auto-dismissing toast ${id} after ${toast.duration}ms`);
        dispatch({ type: 'REMOVE_TOAST', payload: id });
        toastTimers.delete(id);
      }, toast.duration);
      
      // Store timer globally to persist across re-renders
      toastTimers.set(id, timeoutId);
    }

    return id;
  }, []);

  // Listen for toast service events
  React.useEffect(() => {
    const handleToastServiceEvent = (event: CustomEvent) => {
      const { type, title, message, duration } = event.detail;
      console.log(`[TOAST DEBUG] ToastService event received:`, { type, title, message, duration });
      addToast({ type, title, message, duration });
    };

    console.log(`[TOAST DEBUG] ToastProvider mounted - listening for toast-service events`);
    window.addEventListener('toast-service', handleToastServiceEvent as EventListener);
    return () => {
      console.log(`[TOAST DEBUG] ToastProvider unmounting - removing toast-service listener`);
      window.removeEventListener('toast-service', handleToastServiceEvent as EventListener);
    };
  }, [addToast]);


  const removeToast = useCallback((id: string) => {
    console.log(`[TOAST DEBUG] Manually removing toast ${id}`);
    
    // Clear the timer for this toast
    const timer = toastTimers.get(id);
    if (timer) {
      console.log(`[TOAST DEBUG] Clearing timer for manually removed toast ${id}`);
      clearTimeout(timer);
      toastTimers.delete(id);
    }
    
    dispatch({ type: 'REMOVE_TOAST', payload: id });
  }, []);

  const clearAll = useCallback(() => {
    console.log(`[TOAST DEBUG] Clearing all toasts and timers`);
    
    // Clear all timers
    for (const [id, timer] of toastTimers.entries()) {
      clearTimeout(timer);
    }
    toastTimers.clear();
    
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  // Convenience methods
  const success = useCallback((title: string, message?: string) => 
    addToast({ type: 'success', title, message }), [addToast]);

  const error = useCallback((title: string, message?: string) => 
    addToast({ type: 'error', title, message }), [addToast]);

  const warning = useCallback((title: string, message?: string) => 
    addToast({ type: 'warning', title, message }), [addToast]);

  const info = useCallback((title: string, message?: string) => 
    addToast({ type: 'info', title, message }), [addToast]);

  const loading = useCallback((title: string, message?: string) => 
    addToast({ type: 'loading', title, message, duration: 0 }), [addToast]);

  const value = {
    toasts: state.toasts,
    addToast,
    removeToast,
    clearAll,
    success,
    error,
    warning,
    info,
    loading,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

const ToastContainer: React.FC = () => {
  const context = useContext(ToastContext);
  const [navbarHeight, setNavbarHeight] = React.useState(72); // Default fallback
  
  if (!context) return null;

  const { toasts } = context;

  // Calculate actual navbar height dynamically
  React.useEffect(() => {
    const calculateNavbarHeight = () => {
      const navbar = document.querySelector('nav');
      if (navbar) {
        const height = navbar.offsetHeight;
        setNavbarHeight(height);
      }
    };

    calculateNavbarHeight();
    window.addEventListener('resize', calculateNavbarHeight);
    
    return () => {
      window.removeEventListener('resize', calculateNavbarHeight);
    };
  }, []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div 
      className="fixed right-4 z-[55] flex flex-col space-y-3 pointer-events-none"
      style={{ top: `${navbarHeight + 8}px` }}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} />
      ))}
    </div>,
    document.body
  );
};

const ToastItem: React.FC<{ toast: Toast }> = ({ toast }) => {
  const context = useContext(ToastContext);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      context?.removeToast(toast.id);
    }, 200);
  };

  const handleActionClick = () => {
    if (toast.action?.onClick) {
      toast.action.onClick();
      handleDismiss(); // Close notification after action
    }
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return (
          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="w-5 h-5 rounded-full bg-yellow-500 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      case 'info':
        return (
          <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'loading':
        return (
          <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
            <svg className="w-3 h-3 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={`
        relative bg-popover border border-border
        rounded-md shadow-md max-w-sm w-full pointer-events-auto
        transform transition-all duration-200 ease-out
        ${isVisible 
          ? 'translate-x-0 opacity-100 scale-100' 
          : 'translate-x-full opacity-0 scale-95'
        }
      `}
    >
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-0.5">
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-popover-foreground leading-tight">
              {toast.title}
            </p>
            {toast.message && (
              <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                {toast.message}
              </p>
            )}
            
            {/* Action Button */}
            {toast.action && (
              <button
                onClick={handleActionClick}
                className="mt-3 inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
              >
                {toast.action.label}
              </button>
            )}
          </div>
          {toast.dismissible && (
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 ml-4 p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
